// src/pages/Dashboard/Dashboard.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { Typography } from 'antd';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import {
  WalletOutlined,
  SettingOutlined,
  MoneyCollectOutlined,
  SunOutlined,
  MoonOutlined,
  BankOutlined,
  TeamOutlined,
  TrophyOutlined,
  BarChartOutlined
} from '@ant-design/icons';
import { useUserStore } from '../../shared/stores/useUserStore';
import { useAdminSelector } from '../../contexts/AdminSelectorContext';

const { Title, Text } = Typography;

// Definir el tipo para nuestros temas personalizados
type ThemeType = 'dark' | 'light';

// Styled Components
const DashboardContainer = styled.div<{ customtheme: ThemeType }>`
  padding: 24px;
  min-height: 100vh;
  background: ${props => props.customtheme === 'dark' ? '#0a0a0a' : '#f5f5f5'};
`;

const WelcomeSection = styled(motion.div)`
  margin-bottom: 32px;
`;

const WelcomeHeader = styled.div<{ customtheme: ThemeType }>`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;

  .welcome-icon {
    font-size: 32px;
    color: #f8ce46;

    .moon-icon {
      color: #4a90e2 !important;
    }
  }
`;

const WelcomeTitle = styled(Title)<{ customtheme: ThemeType }>`
  margin: 0 !important;
  color: ${props => props.customtheme === 'dark' ? '#ffffff' : '#1e293b'} !important;
  font-size: 28px !important;
  font-weight: 600 !important;
`;

const WelcomeSubtitle = styled(Text)<{ customtheme: ThemeType }>`
  color: ${props => props.customtheme === 'dark' ? '#94a3b8' : '#64748b'} !important;
  font-size: 14px;
`;

const BentoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 16px;
  margin-top: 32px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(6, 1fr);
  }
`;

const BentoCard = styled(motion.div)<{
  customtheme: ThemeType;
  accentcolor: string;
  gridcolumn: string;
  gridrow?: string;
}>`
  grid-column: ${props => props.gridcolumn};
  grid-row: ${props => props.gridrow || 'auto'};
  background: ${props => props.customtheme === 'dark' ? '#1a1a1a' : '#ffffff'};
  border: 1px solid ${props => props.customtheme === 'dark' ? '#2d2d2d' : '#e5e7eb'};
  border-radius: 16px;
  padding: 24px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 140px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${props => props.accentcolor};
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px ${props => props.accentcolor}15;
    border-color: ${props => props.accentcolor};

    &::before {
      transform: scaleX(1);
    }
  }

  @media (max-width: 768px) {
    grid-column: span 6 !important;
    grid-row: auto !important;
  }
`;

const CardIcon = styled.div<{ bgColor: string }>`
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: ${props => props.bgColor}15;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;

  .anticon {
    font-size: 28px;
    color: ${props => props.bgColor};
  }
`;

const CardTitle = styled(Text)<{ customtheme: ThemeType }>`
  color: ${props => props.customtheme === 'dark' ? '#ffffff' : '#1e293b'} !important;
  font-size: 16px !important;
  font-weight: 600 !important;
  display: block;
`;

// Función para obtener el saludo según la hora
const getGreeting = (hour: number): { text: string, icon: React.ReactNode } => {
  if (hour >= 5 && hour < 12) {
    return { text: 'Buenos días', icon: <SunOutlined /> };
  } else if (hour >= 12 && hour < 20) {
    return { text: 'Buenas tardes', icon: <SunOutlined /> };
  } else {
    return {
      text: 'Buenas noches',
      icon: <span className="moon-icon"><MoonOutlined /></span>
    };
  }
};

// Función para formatear la hora de Buenos Aires
const formatBuenosAiresTime = (): string => {
  const options: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Argentina/Buenos_Aires'
  };
  return new Date().toLocaleString('es-AR', options);
};

export const Dashboard: React.FC = () => {
  const [greeting, setGreeting] = useState<{ text: string, icon: React.ReactNode }>({ text: '', icon: null });
  const [buenosAiresTime, setBuenosAiresTime] = useState<string>('');
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { isSuperAdmin } = useAdminSelector();

  const { user } = useUserStore();
  const userName = user?.username || 'Usuario';

  // Actualizar el saludo y la hora cada minuto
  useEffect(() => {
    const updateTimeAndGreeting = () => {
      const now = new Date();
      const buenosAiresTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/Argentina/Buenos_Aires' }));
      const hour = buenosAiresTime.getHours();

      setGreeting(getGreeting(hour));
      setBuenosAiresTime(formatBuenosAiresTime());
    };

    updateTimeAndGreeting();
    const intervalId = setInterval(updateTimeAndGreeting, 60000);

    return () => clearInterval(intervalId);
  }, []);

  // Quick Access Items con layout bento grid
  const quickAccessItems = [
    {
      title: 'Analytics',
      icon: <BarChartOutlined />,
      route: '/user-panel/analytics',
      color: '#1890ff',
      gridColumn: 'span 4',
      gridRow: 'span 2',
      show: isSuperAdmin
    },
    {
      title: 'Requests',
      icon: <BankOutlined />,
      route: '/user-panel/requests',
      color: '#52c41a',
      gridColumn: 'span 3',
      show: true
    },
    {
      title: 'Operations',
      icon: <MoneyCollectOutlined />,
      route: '/user-panel/operations',
      color: '#fa8c16',
      gridColumn: 'span 3',
      show: true
    },
    {
      title: 'Users',
      icon: <TeamOutlined />,
      route: '/user-panel/users',
      color: '#722ed1',
      gridColumn: 'span 2',
      show: true
    },
    {
      title: 'Wallet',
      icon: <WalletOutlined />,
      route: '/user-panel/wallet',
      color: '#eb2f96',
      gridColumn: 'span 3',
      show: true
    },
    {
      title: 'Gamification',
      icon: <TrophyOutlined />,
      route: '/user-panel/gamification',
      color: '#13c2c2',
      gridColumn: 'span 3',
      show: true
    },
    {
      title: 'Settings',
      icon: <SettingOutlined />,
      route: '/user-panel/settings',
      color: '#faad14',
      gridColumn: 'span 4',
      show: true
    }
  ].filter(item => item.show);

  return (
    <DashboardContainer customtheme={theme as ThemeType}>
      {/* Welcome Section */}
      <WelcomeSection
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <WelcomeHeader customtheme={theme as ThemeType}>
          <div className="welcome-icon">{greeting.icon}</div>
          <div>
            <WelcomeTitle level={2} customtheme={theme as ThemeType}>
              {greeting.text}, {userName}
            </WelcomeTitle>
            <WelcomeSubtitle customtheme={theme as ThemeType}>
              {buenosAiresTime} (Buenos Aires) • {new Date().toLocaleDateString('es-AR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </WelcomeSubtitle>
          </div>
        </WelcomeHeader>
      </WelcomeSection>

      {/* Bento Grid Quick Access */}
      <BentoGrid>
        {quickAccessItems.map((item, index) => (
          <BentoCard
            key={index}
            customtheme={theme as ThemeType}
            accentcolor={item.color}
            gridcolumn={item.gridColumn}
            gridrow={item.gridRow}
            onClick={() => navigate(item.route)}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: index * 0.05 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div>
              <CardIcon bgColor={item.color}>
                {item.icon}
              </CardIcon>
              <CardTitle customtheme={theme as ThemeType}>
                {item.title}
              </CardTitle>
            </div>
          </BentoCard>
        ))}
      </BentoGrid>
    </DashboardContainer>
  );
};
